
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export const useProductScanner = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const compressImage = async (file: File): Promise<string> => {
    console.log('Starting image compression:', {
      fileName: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      type: file.type
    });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const MAX_DIMENSION = 1024;
          
          if (width > height && width > MAX_DIMENSION) {
            height *= MAX_DIMENSION / width;
            width = MAX_DIMENSION;
          } else if (height > MAX_DIMENSION) {
            width *= MAX_DIMENSION / height;
            height = MAX_DIMENSION;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          const base64String = canvas.toDataURL('image/jpeg', 0.7);
          
          console.log('Compression complete:', {
            originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
            compressedSize: `${(base64String.length * 0.75 / 1024 / 1024).toFixed(2)}MB`
          });

          resolve(base64String.split(',')[1]);
        };
        img.onerror = (error) => {
          console.error('Error loading image:', error);
          reject(new Error('Failed to load image'));
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  };

  const scanImage = async (file: File) => {
    if (!file || !user) {
      console.error('No image selected for scanning or user not logged in');
      toast({
        title: t("errors.scanError"),
        description: t("errors.noImageSelected"),
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    console.log('Starting scan process:', {
      fileName: file.name,
      fileType: file.type,
      fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`
    });

    try {
      // First create a scan record
      const { data: scanData, error: scanError } = await supabase
        .from('scans')
        .insert({
          user_id: user.id,
          status: 'pending'
        })
        .select()
        .single();

      if (scanError) throw scanError;

      const base64Data = await compressImage(file);
      
      // Call the edge function
      const { data, error } = await supabase.functions.invoke('scan-product', {
        body: {
          image: base64Data,
          scanId: scanData.id,
          metadata: {
            filename: file.name,
            type: file.type,
            size: file.size,
            timestamp: new Date().toISOString(),
            userId: user.id
          }
        }
      });

      console.log('Edge Function response:', { 
        hasData: !!data,
        hasError: !!error,
        errorDetails: error?.message,
        productInfo: data?.productInfo,
        productsCount: data?.products?.length
      });

      if (error) throw error;

      const { detectedText, products, productInfo } = data;

      // Update scan record with results
      await supabase
        .from('scans')
        .update({
          ocr_result: detectedText,
          status: 'completed',
          product_id: products?.[0]?.id // Link to first matching product if any
        })
        .eq('id', scanData.id);

      if (detectedText || productInfo) {
        if (products && products.length > 0) {
          // Product found in database
          console.log('Product(s) found in database:', products.length);
          toast({
            title: t("products.productFound"),
            description: t("products.matchesFound", { count: products.length }),
          });
          return { 
            detectedText, 
            products, 
            scanId: scanData.id,
            productInfo 
          };
        } else {
          // No product found but we have text
          console.log('No product found in database, but text was detected');
          toast({
            title: t("products.scanComplete"),
            description: t("products.noProductFound"),
          });
          return { 
            detectedText, 
            products: [], 
            scanId: scanData.id,
            productInfo 
          };
        }
      } else {
        toast({
          title: t("errors.noTextDetected"),
          description: t("errors.pleaseTryDifferentImage"),
          variant: "destructive",
        });
        return null;
      }
    } catch (error) {
      console.error('Error during scan process:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        }
      });

      let errorMessage = t("errors.pleaseTryAgain");
      
      if (error instanceof Error) {
        if (error.message.includes('Vision API')) {
          errorMessage = t("errors.visionApiError");
        } else if (error.message.includes('No text detected')) {
          errorMessage = t("errors.noTextDetected");
        }
      }

      toast({
        title: t("errors.scanError"),
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
      console.log('Scan process completed');
    }
  };

  return {
    scanImage,
    isLoading
  };
};
